/**
 * Extract UUID from full messageId format
 * @param messageId - Full messageId like "messages/d1919a34-8e88-45b4-936d-120dd6c23a99"
 * @returns UUID portion only
 */
function extractMessageUUID(messageId: string): string {
  if (messageId.startsWith("messages/")) {
    return messageId.substring("messages/".length);
  }
  return messageId; // Already clean or empty
}

/**
 * Extract UUID from full threadId format
 * @param threadId - Full threadId like "threads/20c4ead0-d139-44fb-affb-cb3d95932867"
 * @returns UUID portion only
 */
export function extractThreadUUID(threadId: string): string {
  if (threadId.startsWith("threads/")) {
    return threadId.substring("threads/".length);
  }
  return threadId; // Already clean or empty
}

/**
 * Build full threadId from UUID
 * @param uuid - UUID portion only
 * @returns Full threadId like "threads/UUID"
 */
export function buildFullThreadId(uuid: string): string {
  if (!uuid) {
    return "";
  }
  const decodedUuid = decodeURIComponent(uuid);
  if (decodedUuid.startsWith("threads/")) {
    return decodedUuid; // Already has prefix
  }
  return `threads/${decodedUuid}`;
}

/**
 * Build full messageId from UUID
 * @param uuid - UUID portion only
 * @returns Full messageId like "messages/UUID"
 */
function buildFullMessageId(uuid: string): string {
  if (!uuid) {
    return "";
  }
  const decodedUuid = decodeURIComponent(uuid);
  if (decodedUuid.startsWith("messages/")) {
    return decodedUuid;
  } // Already has prefix
  return `messages/${decodedUuid}`;
}

/**
 * Get the full messageId from URL search params
 * Reads "selectedMessageId" param and prepends "messages/" prefix
 */
export function getMessageIdFromUrl(searchParams: URLSearchParams): string {
  const uuid = searchParams.get("selectedMessageId") || "";
  return buildFullMessageId(uuid);
}

/**
 * Set messageId in URL search params (preserves other params)
 */
export function setMessageIdInUrl(
  searchParams: URLSearchParams,
  messageId: string
): URLSearchParams {
  const newParams = new URLSearchParams(searchParams);

  if (messageId) {
    const uuid = extractMessageUUID(messageId);
    newParams.set("selectedMessageId", uuid);
  } else {
    newParams.delete("selectedMessageId");
  }

  return newParams;
}

/**
 * Build thread link with optional messageId
 * Extracts UUID and stores as "selectedMessageId" param
 */
export function buildThreadLink(
  inboxType: string,
  threadId: string,
  customerId: string,
  messageId?: string
): string {
  // Extract UUID from threadId (handles both formats)
  const uuid = extractThreadUUID(threadId);
  const basePath = `/customers/${customerId}/inbox/${inboxType}/threads/${encodeURIComponent(uuid)}`;

  if (messageId) {
    const msgUuid = extractMessageUUID(messageId);
    return `${basePath}?selectedMessageId=${encodeURIComponent(msgUuid)}`;
  }

  return basePath;
}
